// @ts-nocheck
/**
 * @fileoverview Dexie-backed persistence for TanStack Query caches.
 *
 * Persists successful query results to IndexedDB (via Dexie) so the app can
 * hydrate instantly on cold boots and resume offline. Falls back to an
 * in-memory map when IndexedDB is unavailable (tests, private browsing, etc.).
 *
 * Key features:
 * - Writes are throttled and resilient to storage failures.
 * - Per-query opt-out via `meta.persist === false`.
 * - Optional per-query max age via `meta.persistMaxAge` (milliseconds).
 * - Automatic purge on schema/version changes.
 * - Helpers to clear persisted state per-user when signing out.
 */

import DexieLib from 'dexie';
import logger from '../../utils/logger';

const hasIndexedDB = typeof indexedDB !== 'undefined';
const DB_NAME = 'timegenie_query_cache';
const TABLE_NAME = 'queries';
const SCHEMA_VERSION = 1;
const DEFAULT_MAX_AGE_MS = 6 * 60 * 60 * 1000; // 6 hours

/**
 * @typedef {import('@tanstack/react-query').Query} Query
 * @typedef {import('@tanstack/react-query').QueryClient} QueryClient
 */

/**
 * @typedef {Object} PersistedQueryRecord
 * @property {string} hash
 * @property {string} key
 * @property {any} data
 * @property {number} dataUpdatedAt
 * @property {number} persistedAt
 * @property {string | null} userId
 * @property {{ maxAge?: number, version?: string | number } | undefined} persistMeta
 * @property {number} schemaVersion
 */

/** @type {DexieLib | null} */
let dexie = null;

/**
 * Lazily create the Dexie database if IndexedDB is available.
 */
function ensureDexie() {
  if (!hasIndexedDB) return null;
  if (dexie) return dexie;
  dexie = new DexieLib(DB_NAME);
  dexie.version(1).stores({
    // hash is the primary key; index userId for targeted purges
    [TABLE_NAME]: '&hash, userId, dataUpdatedAt',
  });
  return dexie;
}

/** In-memory fallback store used when IndexedDB is unavailable. */
const memoryStore = new Map();

/**
 * Extract the user id from a query key when present.
 * Keys follow `[ROOT, resource, userId, ...]` convention across the app.
 * @param {unknown} key
 * @returns {string | null}
 */
function extractUserId(key) {
  if (!Array.isArray(key)) return null;
  // Prefer explicit property from object segments (e.g., { userId })
  for (const segment of key) {
    if (segment && typeof segment === 'object' && 'userId' in segment) {
      const val = /** @type {any} */ (segment).userId;
      if (typeof val === 'string' && val) return val;
    }
  }
  // Fall back to third token convention ([ROOT, entity, userId])
  if (typeof key[2] === 'string' && key[2]) {
    return key[2];
  }
  return null;
}

/**
 * Determine whether a query should be persisted.
 * @param {Query} query
 */
function shouldPersist(query) {
  if (!query) return false;
  const state = query.state;
  if (!state || state.status !== 'success') return false;
  if (state.data === undefined) return false;
  const meta = /** @type {{ persist?: boolean }} */ (query.options?.meta || {});
  if (meta && meta.persist === false) return false;
  return true;
}

/**
 * Build a persistence record from a query.
 * @param {Query} query
 * @returns {PersistedQueryRecord | null}
 */
function buildRecord(query) {
  if (!shouldPersist(query)) return null;
  try {
    const key = JSON.stringify(query.queryKey ?? []);
    const state = query.state;
    const meta = /** @type {{ persistMaxAge?: number; persistVersion?: string | number }} */ (
      query.options?.meta || {}
    );
    const record = {
      hash: query.queryHash,
      key,
      data: state.data,
      dataUpdatedAt: state.dataUpdatedAt || Date.now(),
      persistedAt: Date.now(),
      userId: extractUserId(query.queryKey),
      persistMeta: undefined,
      schemaVersion: SCHEMA_VERSION,
    };
    const persistMeta = {};
    if (typeof meta.persistMaxAge === 'number' && Number.isFinite(meta.persistMaxAge)) {
      persistMeta.maxAge = Math.max(0, meta.persistMaxAge);
    }
    if (meta.persistVersion !== undefined && meta.persistVersion !== null) {
      persistMeta.version = meta.persistVersion;
    }
    if (Object.keys(persistMeta).length > 0) {
      record.persistMeta = persistMeta;
    }
    return record;
  } catch (error) {
    logger.warn('QueryCachePersistence: failed to serialize query key', error);
    return null;
  }
}

/**
 * Persist a record to storage (Dexie or memory fallback).
 * @param {PersistedQueryRecord} record
 */
async function persistRecord(record) {
  if (!record) return;
  try {
    const db = ensureDexie();
    if (db) {
      await db.table(TABLE_NAME).put(record);
    } else {
      memoryStore.set(record.hash, record);
    }
  } catch (error) {
    logger.warn('QueryCachePersistence: persist failed', error);
  }
}

/**
 * Remove a persisted record.
 * @param {string} hash
 */
async function removeRecord(hash) {
  if (!hash) return;
  try {
    const db = ensureDexie();
    if (db) {
      await db.table(TABLE_NAME).delete(hash);
    } else {
      memoryStore.delete(hash);
    }
  } catch (error) {
    logger.warn('QueryCachePersistence: failed to delete record', error);
  }
}

/**
 * Load all persisted records from storage.
 * @returns {Promise<PersistedQueryRecord[]>}
 */
async function loadRecords() {
  try {
    const db = ensureDexie();
    if (db) {
      return await db.table(TABLE_NAME).toArray();
    }
    return Array.from(memoryStore.values());
  } catch (error) {
    logger.warn('QueryCachePersistence: failed to load records', error);
    return [];
  }
}

/**
 * Compute max age for a record, falling back to default window.
 * @param {PersistedQueryRecord} record
 */
function getMaxAge(record) {
  if (record?.persistMeta && typeof record.persistMeta.maxAge === 'number') {
    return Math.max(0, record.persistMeta.maxAge);
  }
  return DEFAULT_MAX_AGE_MS;
}

/**
 * Determine whether a record is still fresh enough to hydrate.
 * @param {PersistedQueryRecord} record
 */
function isRecordFresh(record) {
  if (!record) return false;
  if (record.schemaVersion !== SCHEMA_VERSION) return false;
  const maxAge = getMaxAge(record);
  if (maxAge === 0) return true;
  const ts = record.dataUpdatedAt || record.persistedAt || 0;
  if (!ts) return false;
  return Date.now() - ts <= maxAge;
}

/**
 * Apply a record to a query client.
 * @param {QueryClient} queryClient
 * @param {PersistedQueryRecord} record
 */
function hydrateRecord(queryClient, record) {
  if (!record) return;
  try {
    const key = JSON.parse(record.key || '[]');
    queryClient.setQueryData(key, record.data, {
      updatedAt: record.dataUpdatedAt || record.persistedAt || Date.now(),
    });
  } catch (error) {
    logger.warn('QueryCachePersistence: failed to hydrate record', error);
  }
}

/** Track which clients have already been wired up to avoid duplicate subscriptions. */
const wiredClients = new WeakSet();

/** Pending timers per query hash to coalesce writes. */
const pendingTimers = new Map();

/**
 * Schedule a persistence write for a query with small debounce to avoid churn.
 * @param {Query} query
 */
function schedulePersist(query) {
  const hash = query?.queryHash;
  if (!hash) return;
  if (pendingTimers.has(hash)) {
    clearTimeout(/** @type {number} */ (pendingTimers.get(hash)));
  }
  const timer = setTimeout(() => {
    pendingTimers.delete(hash);
    const record = buildRecord(query);
    if (record) {
      void persistRecord(record);
    } else {
      void removeRecord(hash);
    }
  }, 150);
  pendingTimers.set(hash, /** @type {number} */ (timer));
}

/**
 * Cancel any pending persistence timer for a query hash.
 * @param {string} hash
 */
function cancelPending(hash) {
  if (!pendingTimers.has(hash)) return;
  clearTimeout(/** @type {number} */ (pendingTimers.get(hash)));
  pendingTimers.delete(hash);
}

/**
 * Hydrate the provided query client and register persistence listeners.
 * @param {QueryClient} queryClient
 * @returns {Promise<{ hydrated: boolean, unsubscribe: () => void }>}
 */
export async function setupQueryCachePersistence(queryClient) {
  if (!queryClient) {
    return { hydrated: false, unsubscribe: () => {} };
  }

  if (wiredClients.has(queryClient)) {
    return { hydrated: false, unsubscribe: () => {} };
  }
  wiredClients.add(queryClient);

  let hydrated = false;
  try {
    const records = await loadRecords();
    if (records.length > 0) {
      for (const record of records) {
        if (isRecordFresh(record)) {
          hydrateRecord(queryClient, record);
          hydrated = true;
        } else {
          void removeRecord(record?.hash);
        }
      }
    }
  } catch (error) {
    logger.warn('QueryCachePersistence: hydration failed', error);
  }

  const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
    const query = event?.query;
    if (!query) return;
    const hash = query.queryHash;
    switch (event.type) {
      case 'removed':
        cancelPending(hash);
        void removeRecord(hash);
        break;
      case 'added':
      case 'updated':
        schedulePersist(query);
        break;
      default:
        break;
    }
  });

  return { hydrated, unsubscribe };
}

/**
 * Clear persisted query cache. When a userId is provided, only entries tagged
 * with that user id are removed.
 * @param {{ userId?: string | null }} [options]
 */
export async function clearPersistedQueryCache(options = {}) {
  const { userId } = options;
  try {
    const db = ensureDexie();
    if (db) {
      const table = db.table(TABLE_NAME);
      if (userId) {
        await table.where('userId').equals(String(userId)).delete();
      } else {
        await table.clear();
      }
    } else if (userId) {
      for (const [hash, record] of memoryStore.entries()) {
        if (record?.userId && String(record.userId) === String(userId)) {
          memoryStore.delete(hash);
        }
      }
    } else {
      memoryStore.clear();
    }
  } catch (error) {
    logger.warn('QueryCachePersistence: failed to clear cache', error);
  }
}
export default {
  setupQueryCachePersistence,
  clearPersistedQueryCache,
};
